import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trash2, Share2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore, useAppStore, useProjectsStore } from '../store';
import { T } from '../i18n';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { lang, setCurrentObject, setPhoto, setIaImage } = useAppStore();
  const { projects, loading, fetchProjects, deleteProject } = useProjectsStore();
  const t = T[lang] || T.fr;
  
  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user, fetchProjects]);
  
  const handleResume = (project) => {
    // Load project into app state
    setCurrentObject(project.object_type);
    if (project.photo_base64) {
      setPhoto(`data:image/jpeg;base64,${project.photo_base64}`, project.photo_base64);
    }
    if (project.ia_image_url) {
      useAppStore.getState().iaStack.push(project.ia_image_url);
      useAppStore.getState().iaIndex = useAppStore.getState().iaStack.length - 1;
    }
    // Update config
    if (project.config) {
      useAppStore.getState().updateConfig(project.object_type, project.config);
    }
    navigate('/result');
  };
  
  const handleDelete = async (projectId) => {
    if (window.confirm('Supprimer ce projet ?')) {
      await deleteProject(projectId);
      toast.success('Projet supprimé');
    }
  };
  
  const handleShare = async (project) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: project.name,
          text: `Ma simulation ${project.name} - SoView-FX`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Lien copié !');
      }
    } catch (err) {
      // Cancelled
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang, { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getObjectLabel = (type) => {
    const labels = {
      store: t.store,
      pergola: t.pergola,

    };
    return labels[type] || type;
  };
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-lg hover:bg-white/5"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">
          {t.my_projects}
        </h1>
      </header>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-safe">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="spinner" />
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <p>{t.no_projects}</p>
          </div>
        ) : (
          projects.map((project, i) => (
            <motion.div
              key={project.project_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="card p-3 flex gap-3"
              data-testid={`project-card-${project.project_id}`}
            >
              {/* Thumbnail */}
              <div className="w-16 h-12 rounded-lg bg-background-tertiary overflow-hidden flex-shrink-0">
                {project.thumbnail ? (
                  <img 
                    src={project.thumbnail} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : project.ia_image_url ? (
                  <img 
                    src={project.ia_image_url} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                    {getObjectLabel(project.object_type)?.[0]}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{project.name}</p>
                <p className="text-xs text-muted-foreground">
                  {getObjectLabel(project.object_type)} • {formatDate(project.created_at)}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleResume(project)}
                  className="p-2 rounded-lg bg-secondary/20 text-secondary"
                  data-testid={`resume-${project.project_id}`}
                >
                  <Play size={16} />
                </button>
                <button
                  onClick={() => handleShare(project)}
                  className="p-2 rounded-lg hover:bg-white/5"
                  data-testid={`share-${project.project_id}`}
                >
                  <Share2 size={16} className="text-muted-foreground" />
                </button>
                <button
                  onClick={() => handleDelete(project.project_id)}
                  className="p-2 rounded-lg hover:bg-red-500/10"
                  data-testid={`delete-${project.project_id}`}
                >
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
